import React from 'react';
import Modal from 'react-bootstrap/Modal';
import { Col, Form, Row, Spinner, Stack, Table } from "react-bootstrap";
import { useContext, useState, useRef, ChangeEvent } from 'react';
import { DrinkUpServiceApiContext } from '../../DrinkUpServiceApiContext';
import { UserContext } from '../../hooks/useUser';
import { IndependentBottlerRow } from '../UserPage/UserProfileModal';
import { AdminUser, Rum } from '../../types';

type UserRum = Rum & { approved_on?: string; is_immortal?: boolean };

type UserRumSortField = 'rumID' | 'rumName' | 'requestedDate' | 'approvalDate' | 'approver';

interface AdminUserModalProps {
    closeModal: () => void;
    show: boolean;
    user: AdminUser | null;
    rums: Rum[];
}

function approvalOrRejectedDate(rum: UserRum): string | null {
    if (rum.rejected_on) {
        return rum.rejected_on;
    }
    if (rum.approved_on) {
        return rum.approved_on;
    }
    return null;
}

function matchesRumSearch(rum: UserRum, search: string): boolean {
    if (!search.trim()) {
        return true;
    }
    const terms = search.trim().split(/\s+/);
    for (const term of terms) {
        const upper = term.toUpperCase();
        const idMatch = (rum.inventory_id ?? '').toUpperCase().includes(upper);
        const nameMatch = rum.bevager_name.toUpperCase().includes(upper);
        if (!idMatch && !nameMatch) {
            return false;
        }
    }
    return true;
}

function requestStatusColor(rum: UserRum): string {
    if (rum.rejected_on) {
        return "red";
    }
    if (rum.approved_on) {
        return "green";
    }
    if (rum.requested_on) {
        return "gray";
    }
    return "red";
}

export default function AdminUserModal({ closeModal, show, user, rums}: AdminUserModalProps): JSX.Element {    

    const { authToken } = useContext(UserContext);
    const { drinkUpService } = useContext(DrinkUpServiceApiContext);

    const [userRums, setUserRums] = useState<UserRum[]>([]);
    const [rumSearchText, setRumSearchText] = useState('');

    const isInitialLoad = useRef(true);
    const isDoneLoading = useRef(false);
    const currentUserId = useRef(0);
    const userRumSortField = useRef<UserRumSortField>('approvalDate');
    const userRumSortAscending = useRef(false);
    
    if(!user) {
        return (
            <></>
        )
    }

    if(currentUserId.current !== user.user) {
        currentUserId.current = user.user;
        isInitialLoad.current = true;
        isDoneLoading.current = false;
        userRumSortField.current = 'approvalDate';
        userRumSortAscending.current = false;
        setRumSearchText('');
    }

    function compareUserRums(a: UserRum, b: UserRum): number {
        const ascending = userRumSortAscending.current;
        const flip = (cmp: boolean): number => ascending ? (cmp ? 1 : -1) : (cmp ? -1 : 1);

        if (userRumSortField.current === 'rumID') {
            const aId = (a.inventory_id ?? '').toUpperCase();
            const bId = (b.inventory_id ?? '').toUpperCase();
            return flip(aId < bId);
        }
        if (userRumSortField.current === 'rumName') {
            return flip(a.bevager_name.toUpperCase() < b.bevager_name.toUpperCase());
        }
        if (userRumSortField.current === 'requestedDate') {
            const aDate = a.requested_on ? Date.parse(a.requested_on) : 0;
            const bDate = b.requested_on ? Date.parse(b.requested_on) : 0;
            return flip(aDate < bDate);
        }
        if (userRumSortField.current === 'approvalDate') {
            const aDate = approvalOrRejectedDate(a) ? Date.parse(approvalOrRejectedDate(a)!) : 0;
            const bDate = approvalOrRejectedDate(b) ? Date.parse(approvalOrRejectedDate(b)!) : 0;
            return flip(aDate < bDate);
        }
        if (userRumSortField.current === 'approver') {
            const aApprover = (a.approver ?? '').toUpperCase();
            const bApprover = (b.approver ?? '').toUpperCase();
            return flip(aApprover < bApprover);
        }
        return 0;
    }

    function applySort(rums: UserRum[]): UserRum[] {
        const sorted = [...rums];
        sorted.sort(compareUserRums);
        return sorted;
    }

    function sortUserRums(field: UserRumSortField): void {
        if (userRumSortField.current === field) {
            userRumSortAscending.current = !userRumSortAscending.current;
        }
        userRumSortField.current = field;
        setUserRums(prev => applySort(prev));
    }

    const loadRequests = (): void => {
        
        console.log("loading rum requests");

        drinkUpService.get(`/get_user_rums?userId=${user.user}`, {
            headers: {
                'Authorization': `Token ${authToken}`,
                'Content-Type': 'application/json'
            }
        })
        .then(response => {
            console.log("num rum requests: " + response.data.length);
            console.log("requests: " + JSON.stringify(response.data));
            isDoneLoading.current = true;

            const userRumsArray = response.data as UserRum[];
            const userRumsRequested = userRumsArray.filter((request) => !!request.requested_on);
            userRumSortField.current = 'approvalDate';
            userRumSortAscending.current = false;
            setUserRums(applySort(userRumsRequested));
        })
        .catch(err => {
            console.log(err);
        });
    }

    function formattedDateTime(dateString: string): string {
        const date = new Date(Date.parse(dateString));

        let ampm = "AM";

        let hours = date.getHours();
        if(hours > 12) {
            hours = hours - 12;
            ampm = "PM";
        } else if(hours === 0) {
            hours = 12;
        } else if(hours === 12) {
            ampm = "PM";
        }

        return date.toDateString() + ", " + hours + ":" + String(date.getMinutes()).padStart(2, '0') + " " + ampm;
    }

    if (isInitialLoad.current) {
        isInitialLoad.current = false;
        loadRequests();
    }

    if (!isDoneLoading.current) {
        return (
            <>
            <Modal show={show} onHide={closeModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{user.first_name} {user.last_name}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row className="justify-content-center align-items-center align-self-center mt-4">
                        <Col>
                            <div className="d-flex align-items-center align-self-center justify-content-center">
                                <Spinner animation="border" variant="primary" />
                            </div>
                        </Col>
                    </Row>
                </Modal.Body>
            </Modal>
            </>
        )
    }

    const numSignedRums = userRums.filter(rum => !!rum.approved_on).length;
    const numAwaitingApproval = userRums.filter(rum => !rum.approved_on && rum.requested_on).length;
    const filteredUserRums = userRums.filter(rum => matchesRumSearch(rum, rumSearchText));

    const onRumSearchChange = (event: ChangeEvent<HTMLInputElement>): void => {
        setRumSearchText(event.target.value);
    };

    return (
        <>
        <Modal show={show} onHide={closeModal} size="lg">
            <Modal.Header closeButton>
            <Modal.Title>{user.first_name} {user.last_name}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Stack direction="horizontal">
                    <div className="p-2">Total Signed Rums:</div>
                    <div className="p-2 ms-auto">{numSignedRums}</div>
                </Stack>
                <Stack direction="horizontal">
                    <div className="p-2">Rums awaiting approval:</div>
                    <div className="p-2 ms-auto">{numAwaitingApproval}</div>
                </Stack>

                <div className="p-2" style={{borderBottom: "1px solid grey"}}>
                    <label className="fw-bold">INDEPENDENT BOTTLERS</label>
                    <IndependentBottlerRow label={"Blackadder"} rums={userRums} needed={15} filter={(rum) => rum.bevager_name.toLowerCase().includes("blackadder") && "approver" in rum && rum.rejected_on == null}/>
                    <IndependentBottlerRow label={"Boutique-y"} rums={userRums} needed={18} filter={(rum) => rum.bevager_name.toLowerCase().includes("boutique") && "approver" in rum && rum.rejected_on == null}/>
                    <IndependentBottlerRow label={"Bristol Classic/Bristol Spirits"} rums={userRums} needed={17} filter={(rum) => rum.bevager_name.toLowerCase().includes("bristol") && "approver" in rum && rum.rejected_on == null}/>
                    <IndependentBottlerRow label={"Cadenhead"} rums={userRums} needed={14} filter={(rum) => rum.bevager_name.toLowerCase().includes("cadenhead") && "approver" in rum && rum.rejected_on == null}/>
                    <IndependentBottlerRow label={"Colours of Rum"} rums={userRums} needed={14} filter={(rum) => rum.bevager_name.toLowerCase().includes("colours of rum") && "approver" in rum && rum.rejected_on == null}/>
                    <IndependentBottlerRow label={"Duncan Taylor"} rums={userRums} needed={8} filter={(rum) => rum.bevager_name.toLowerCase().includes("duncan") && "approver" in rum && rum.rejected_on == null}/>
                    <IndependentBottlerRow label={"Hamilton"} rums={userRums} needed={15} filter={(rum) => rum.bevager_name.toLowerCase().includes("hamilton") && "approver" in rum && rum.rejected_on == null}/>
                    <IndependentBottlerRow label={"Holmes Cay"} rums={userRums} needed={24} filter={(rum) => rum.bevager_name.toLowerCase().includes("holmes cay") && "approver" in rum && rum.rejected_on == null}/>
                    <IndependentBottlerRow label={"Plantation"} rums={userRums} needed={16} filter={(rum) => (rum.bevager_name.toLowerCase().includes("plantation") || rum.bevager_name.toLowerCase().includes("planteray")) && !rum.bevager_name.toLowerCase().includes("grove") && !rum.bevager_name.toLowerCase().includes("myer") && "approver" in rum && rum.rejected_on == null}/>
                    <IndependentBottlerRow label={"Royal Cane"} rums={userRums} needed={13} filter={(rum) => rum.bevager_name.toLowerCase().includes("royal cane") && "approver" in rum && rum.rejected_on == null}/>
                    <IndependentBottlerRow label={"S.B.S"} rums={userRums} needed={15} filter={(rum) => rum.bevager_name.includes("SBS") && "approver" in rum && rum.rejected_on == null}/>
                    <IndependentBottlerRow label={"Samaroli"} rums={userRums} needed={22} filter={(rum) => rum.bevager_name.toLowerCase().includes("samaroli") && "approver" in rum && rum.rejected_on == null}/>
                    <IndependentBottlerRow label={"Velier"} rums={userRums} needed={42} filter={(rum) => (rum.bevager_name.toLowerCase().includes("velier") || rum.bevager_name.toLowerCase().includes("lm&v")) && "approver" in rum && rum.rejected_on == null}/>
                </div>

                <Form.Control
                    type="search"
                    className="mt-3"
                    placeholder="Search by RumID or rum name"
                    value={rumSearchText}
                    onChange={onRumSearchChange}
                />

                <Table className="mt-2">
                    <thead>
                        <tr>
                            <th onClick={() => sortUserRums('rumID')} style={{ cursor: 'pointer' }}>
                                RumID{userRumSortField.current === 'rumID' ? (userRumSortAscending.current ? ' ▲' : ' ▼') : ''}
                            </th>
                            <th onClick={() => sortUserRums('rumName')} style={{ cursor: 'pointer' }}>
                                Rum Name{userRumSortField.current === 'rumName' ? (userRumSortAscending.current ? ' ▲' : ' ▼') : ''}
                            </th>
                            <th onClick={() => sortUserRums('requestedDate')} style={{ cursor: 'pointer' }}>
                                Requested Date{userRumSortField.current === 'requestedDate' ? (userRumSortAscending.current ? ' ▲' : ' ▼') : ''}
                            </th>
                            <th onClick={() => sortUserRums('approvalDate')} style={{ cursor: 'pointer' }}>
                                Approval/Rejected Date{userRumSortField.current === 'approvalDate' ? (userRumSortAscending.current ? ' ▲' : ' ▼') : ''}
                            </th>
                            <th onClick={() => sortUserRums('approver')} style={{ cursor: 'pointer' }}>
                                Responder{userRumSortField.current === 'approver' ? (userRumSortAscending.current ? ' ▲' : ' ▼') : ''}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredUserRums.map((userRum) => {
                            const approvalDate = approvalOrRejectedDate(userRum);
                            return (
                                <tr key={userRum.id}>
                                    <td>{userRum.inventory_id ?? '—'}</td>
                                    <td className={userRum.is_immortal ? 'immortal' : 'regular-rum'}>{userRum.bevager_name}</td>
                                    <td>{userRum.requested_on ? formattedDateTime(userRum.requested_on) : '—'}</td>
                                    <td style={{ color: requestStatusColor(userRum) }}>
                                        {approvalDate ? formattedDateTime(approvalDate) : '—'}
                                        {userRum.rejected_on && (
                                            <div className="small text-muted">
                                                {userRum.rejection_reason ?? 'No reason given'}
                                            </div>
                                        )}
                                    </td>
                                    <td style={{ color: requestStatusColor(userRum) }}>
                                        {userRum.approver ?? '—'}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            </Modal.Body>
        </Modal>
        </>
    );
}
